import {
	EntityCreateDrawerButton,
	FormattedCurrency,
	FormattedDateTime,
	IDataListColumn,
	PaginatedAutoDataList,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eventUserRequestService } from "../../../shared/api/eventUserRequestService";
import { EventUserRequestOpenIndexResource } from "../../../shared/types/eventUserRequest";
import { CreateEventUserRequestDrawer } from "./CreateEventUserRequestDrawer";
import { DeclineEventUserRequestDrawer } from "./DeclineEventUserRequestDrawer";
import { EventUserRequestBulkEdit } from "./EventUserRequestBulkEdit";
import { EventUserRequestOpenListContextMenu } from "./EventUserRequestOpenListContextMenu";

export const EventUserRequestOpenList: FC = () => {
	const { t } = useTranslation();
	const drawerProps = useEditDrawer();

	const dataListColumns: IDataListColumn<EventUserRequestOpenIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Event name"),
				valueKey: "event.name",
				type: "value",
			},
			{
				heading: t("Event start"),
				valueKey: "event.start",
				type: "value",
				render: (eventRequest) => <FormattedDateTime dateTime={eventRequest.event.start} />,
			},
			{
				heading: t("Event end"),
				valueKey: "event.end",
				type: "value",
				render: (eventRequest) => <FormattedDateTime dateTime={eventRequest.event.end} />,
			},
			{
				heading: t("User"),
				valueKey: "user",
				type: "value",
				render: (eventRequest) => `${eventRequest.user.name} (${eventRequest.user.email})`,
			},
			{
				heading: t("Participation reason"),
				valueKey: "participationReason",
				type: "value",
			},
			{
				heading: t("Costs"),
				valueKey: "costs",
				type: "value",
				render: (eventRequest) => <FormattedCurrency value={eventRequest.costs} currencyType={eventRequest.currency} />,
			},
			{
				heading: t("Cost Center"),
				valueKey: "costCenterName",
				type: "value",
			},
			{
				heading: t("User cost approval"),
				valueKey: "requestedApprover",
				type: "value",
				render: (eventRequest) => `${eventRequest.requestedApprover.name} (${eventRequest.requestedApprover.email})`,
			},
			{
				heading: t("Created at"),
				valueKey: "createdAt",
				type: "value",
				render: ({ createdAt }) => <FormattedDateTime dateTime={createdAt} />,
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }) => (
					<EventUserRequestOpenListContextMenu
						eventRequestId={id}
						openDeclineDrawer={() => drawerProps.handleOpen(id)}
					/>
				),
			},
		],
		[drawerProps, t],
	);

	return (
		<>
			<PaginatedAutoDataList
				name="events"
				columns={dataListColumns}
				empty={t("No open requests available")}
				heading={t("Open requests")}
				listFetchConfig={laravelPaginatedListFetchConfig(
					eventUserRequestService.openRequestsKey,
					eventUserRequestService.indexOpenRequests,
				)}
				initialSearch={{
					search: "",
					searchFields: [
						{ label: t("Event"), name: "event_translations.name" },
						{ label: t("User"), name: "user" },
					],
				}}
				headerActions={
					<EntityCreateDrawerButton renderDrawer={(drawerProps) => <CreateEventUserRequestDrawer {...drawerProps} />} />
				}
				bulkEditActions={<EventUserRequestBulkEdit />}
				enableBulkEdit
			/>
			{drawerProps.editEntityId && (
				<DeclineEventUserRequestDrawer {...drawerProps} editEntityId={drawerProps.editEntityId} />
			)}
		</>
	);
};
